/**
 * AttributionModal — the admin attribution picker. When a SUPERADMIN mutates a
 * creation they are NOT a member of, the server requires `attributeTo`: the
 * member (creator or an editor) the change is credited to. This modal lists
 * the creation's collaborators as radios and confirms the pick; the caller
 * then passes the chosen uid to the callable. The admin's own identity still
 * lands in the audit log as `actionPerformedBy`.
 */
import { useState } from 'react';
import type { Creation } from '@djgg/creations';
import { Modal } from './Modal.js';

export interface AttributionModalProps {
  creation: Creation & { id: string };
  /** Called with the picked collaborator's uid. */
  onConfirm: (attributeTo: string) => void;
  onCancel: () => void;
}

export function AttributionModal({ creation, onConfirm, onCancel }: AttributionModalProps) {
  const [picked, setPicked] = useState('');
  const collaborators = [
    { uid: creation.creatorUid, handle: creation.creatorHandle, role: 'creator' as const },
    ...(creation.editors ?? []).map((e) => ({ uid: e.uid, handle: e.handle, role: 'editor' as const })),
  ];

  return (
    <Modal title="Attribute this change" onClose={onCancel}>
      <p className="muted" style={{ marginTop: -6 }}>
        Admin changes are recorded as the selected collaborator; your admin identity is still
        kept in the audit log.
      </p>
      <div className="stack" style={{ margin: '14px 0' }} role="radiogroup" aria-label="Attribute to">
        {collaborators.map((c) => (
          <label
            key={c.uid}
            className="row"
            style={{ cursor: 'pointer' }}
            data-testid={`attribution-option-${c.uid}`}
          >
            <input
              type="radio"
              name="attribute-to"
              value={c.uid}
              checked={picked === c.uid}
              onChange={() => setPicked(c.uid)}
            />
            <span>
              @{c.handle || c.uid} <span className="pill">{c.role}</span>
            </span>
          </label>
        ))}
      </div>
      <div className="row">
        <button
          className="btn btn--primary"
          disabled={!picked}
          onClick={() => onConfirm(picked)}
          data-testid="attribution-confirm"
        >
          Confirm
        </button>
        <button className="btn btn--ghost" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </Modal>
  );
}
